import Header from '../components/Header'
import Footer from '../components/Footer'
import ProductRow from '../components/AllProducts/ProductRow'
import SingleProduct from '../components/AllProducts/SingleProduct'
import NeedHelp from '../components/AllProducts/NeedHelp'
import HeroBanner from '../components/AllProducts/HeroBanner'
import HomepageBookCallLeadModal from '../components/Modals/HomepageBookCallLeadModal'
import { first } from 'lodash'
import { ProductAPI } from '../apis/server/ProductAPI'
import { GeneralAPI } from '../apis/GeneralAPI'

import Campaigns from '../Icons/allproducts/campaigns.svg'
import Communication from '../Icons/allproducts/communication.svg'
import Handwritten from '../Icons/allproducts/handwritten.svg'

// ------------------------------------
// Section Config
// ------------------------------------
const sections = [
    {
        icon: Campaigns,
        title: 'Multi-Touch Campaigns',
        desc: 'Build consistent, high-performing direct mail campaigns that stay in front of your audience over time. Multi-touch campaigns increase response rates, reinforce brand recognition, and drive more predictable results by delivering the right message at the right cadence — without manual work.',
        products: ['proven-mix-campaigns', 'create-your-own-campaign'],
        background: true,
        id: 'multi-touch-campaigns',
    },
    {
        icon: Handwritten,
        title: 'Postcards',
        desc: 'High-impact postcards designed to grab attention fast and drive immediate action. With four size options, 4x6, 6x9, 6x11, and 9x12…plus multiple postage choices and the ability to add a Street View image and custom offer, this product is flexible enough for almost any campaign.',
        products: [
            'postcards',
            // 'large-postcards',
            // 'ros-postcards',
            // 'ros-postcards-large',
            // 'streetview-postcards',
        ],
        background: false,
        id: 'postcards',
    },
    {
        icon: Handwritten,
        title: 'Real Penned Products',
        desc: 'Create authentic, personal connections at scale with real handwritten mail. These pieces cut through digital noise, increase open rates, and make your outreach feel human — ideal for high-value prospects, retention, and relationship-driven campaigns.',
        products: [
            'real-penned-letters',
            'real-penned-professional-letters',
            'real-penned-postcards',
        ],
        background: false,
        id: 'real-penned-products',
    },
    {
        icon: Communication,
        title: 'Letters',
        desc: 'Polished, professional mail designed to build credibility, trust, and long-term brand presence. Ideal for customer communication, compliance messaging, onboarding, follow-ups, and formal outreach where presentation matters.',
        products: ['professional-letters', 'personal-letters', 'ros-letters'],
        background: true,
        id: 'letters',
    },
    {
        icon: Handwritten,
        title: 'Self Mailers',
        desc: 'Cost-effective, high-visibility mail pieces that deliver your message without an envelope. Great for promotions, product launches, event marketing, and rapid distribution campaigns where speed and simplicity matter.',
        products: ['tri-fold-flyers', 'snap-pack-check-mailers-ros'],
        background: true,
        id: 'self-mailers',
    },
    {
        icon: Handwritten,
        title: 'Offer Products',
        desc: 'Direct-response products designed to drive action with clear offers, personalization, and measurable ROI. Ideal for testing new campaigns, increasing conversion rates, and scaling what works.',
        products: ['snap-pack-check-mailers-ros', 'ros-letters'],
        background: true,
        id: 'offer-products',
    },
]

// ------------------------------------
// Quick Navigation Component
// ------------------------------------
function ProductsQuickNav({ sections }) {
    return (
        <nav className="productsQuickNav">
            <div className="productsQuickNavInner">
                {sections.map((s) => (
                    <a
                        key={s.id}
                        href={`#${s.id}`}
                        className="productsQuickNavLink">
                        {s.title}
                    </a>
                ))}
            </div>
            {/* eslint-disable-next-line react/no-unknown-property */}
            <style jsx>{`
                .productsQuickNav {
                    position: sticky;
                    top: 0;
                    z-index: 2;
                    background: #fff;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                }

                .productsQuickNavInner {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 14px 16px;
                    display: flex;
                    justify-content: center; /* CENTER */
                    gap: 12px;
                    flex-wrap: wrap;
                }

                .productsQuickNavLink {
                    display: inline-flex;
                    align-items: center;
                    padding: 10px 16px;
                    border-radius: 999px;
                    font-size: 14px;
                    font-weight: 700; /* BOLD */
                    line-height: 1;
                    text-decoration: none;

                    background: rgba(237, 92, 47, 0.12); /* light orange */
                    border: 1px solid rgba(237, 92, 47, 0.35);
                    color: #ed5c2f; /* brand orange */
                    transition: all 0.15s ease-in-out;
                }

                .productsQuickNavLink:hover {
                    background: rgba(237, 92, 47, 0.18);
                    border-color: rgba(237, 92, 47, 0.55);
                    transform: translateY(-1px);
                }
            `}</style>
        </nav>
    )
}

// ------------------------------------
// Page Component
// ------------------------------------
export default function Products({ products, meta }) {
    return (
        <>
            <Header meta={meta} metaTitle={'All Products'} />
            <HeroBanner />

            {/* Quick jump navigation */}
            <ProductsQuickNav sections={sections} />

            {sections.map((section) => (
                <section
                    key={section.id}
                    id={section.id}
                    style={{ scrollMarginTop: 110 }}>
                    <ProductRow section={section}>
                        {products
                            .filter((product) =>
                                section.products.includes(
                                    product.product.post_name,
                                ),
                            )
                            .map(({ product, image, description }) => (
                                <SingleProduct
                                    key={product.ID}
                                    {...{ product, image, description }}
                                />
                            ))}
                    </ProductRow>
                </section>
            ))}

            <NeedHelp Modal={HomepageBookCallLeadModal} />
            <Footer />
        </>
    )
}

// ------------------------------------
// Static Props
// ------------------------------------
export async function getStaticProps() {
    const { data: categories } = await ProductAPI.getProductCategory()
    const { yoast_head: meta } = first(categories)
    const products = await GeneralAPI.getACFProductsPage()

    return {
        props: {
            products,
            meta,
        },
        revalidate: 10,
    }
}
